/**
 * SessionStorage cache for feature flags (stale-while-revalidate on cold load).
 *
 * Key: eptai_feature_flags_v1:{userSub}:{accountId}
 * Value: JSON array of ResolvedFeature-like objects.
 */

export type CachedFeatureFlag = {
  flag_key: string;
  name?: string;
  description?: string | null;
  metadata?: Record<string, unknown>;
};

const CACHE_PREFIX = 'eptai_feature_flags_v1:';

/** Build the sessionStorage key for a user + account. */
export function featureFlagsCacheKey(
  userSub: string,
  accountId: string | number | null | undefined
): string {
  return `${CACHE_PREFIX}${userSub}:${accountId ?? 'none'}`;
}

/** Read cached flags; returns null when missing or invalid. */
export function readFeatureFlagsCache(
  userSub: string,
  accountId: string | number | null | undefined
): CachedFeatureFlag[] | null {
  try {
    const raw = sessionStorage.getItem(featureFlagsCacheKey(userSub, accountId));
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return null;
    }
    return parsed.filter(
      (item): item is CachedFeatureFlag =>
        Boolean(item) &&
        typeof item === 'object' &&
        typeof (item as CachedFeatureFlag).flag_key === 'string'
    );
  } catch {
    return null;
  }
}

/** Persist flags for the next cold load of this user/account. */
export function writeFeatureFlagsCache(
  userSub: string,
  accountId: string | number | null | undefined,
  features: CachedFeatureFlag[]
): void {
  try {
    sessionStorage.setItem(
      featureFlagsCacheKey(userSub, accountId),
      JSON.stringify(features)
    );
  } catch {
    // Quota / private mode — ignore
  }
}

/** Remove all feature-flag session cache entries (logout / user switch). */
export function clearFeatureFlagsCache(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}
